import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined, MinusCircleFilled } from '@ant-design/icons';
import { Spin, Tooltip } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { type ReactNode } from 'react';

import { TPO_OPTIMIZATION_STATUS_ICON } from '../config/testIds';

export type OptimizationOutcome = 'optimal' | 'sub_optimal' | 'infeasible';

export type OptimizationStatus = {
    status: 'in_progress' | 'completed' | 'error';
    outcome: OptimizationOutcome | null;
    title?: string | null;
    message: string | null;
} | null;

// AntD v4 pins the `top` arrow to the popup's own midpoint via CSS. The status icon
// sits at the far left of the plan header, so a centred popup overflows the viewport,
// `autoAdjustOverflow` slides it right, and the arrow no longer meets the icon.
// `topLeft` + `arrowPointAtCenter` anchors the popup's left edge near the trigger so it
// grows rightwards and the arrow stays over the icon.
const TOOLTIP_PLACEMENT_PROPS = { placement: 'topLeft', arrowPointAtCenter: true } as const;

const LOADING_ICON_STYLE = { fontSize: 18, color: colorConstants.cyan };
const CHECK_ICON_STYLE = { position: 'relative' as const, top: 0, fontSize: 16 };
const INFEASIBLE_ICON_STYLE = { position: 'relative' as const, top: 0, fontSize: 16, color: colorConstants.fontGreyAlt };
const ERROR_ICON_STYLE = { position: 'relative' as const, fontSize: 16, top: 0, color: '#FF4D4F' };

const OUTCOME_COLOR_MAP: Record<OptimizationOutcome, string> = {
    optimal: colorConstants.limeGreen,
    sub_optimal: colorConstants.yellow,
    infeasible: colorConstants.fontGreyAlt,
};

const OUTCOME_LABEL_MAP: Record<OptimizationOutcome, string> = {
    optimal: 'Optimal Solution Found',
    sub_optimal: 'Sub-optimal Solution Found',
    infeasible: 'No Solution Found',
};

export const getOutcomeColor = (outcome: OptimizationOutcome | null | undefined): string => {
    if (!outcome) return colorConstants.limeGreen;
    return OUTCOME_COLOR_MAP[outcome] ?? colorConstants.limeGreen;
};

const getTooltipText = (status: NonNullable<OptimizationStatus>): string => {
    switch (status.status) {
        case 'in_progress':
            return 'Optimization in progress';
        case 'error':
            return status.message ?? 'Optimization failed';
        case 'completed': {
            const label = status.title ?? (status.outcome ? OUTCOME_LABEL_MAP[status.outcome] : 'Optimization completed');
            return status.message ? `${label}: ${status.message}` : label;
        }
        default:
            return '';
    }
};

export const getOptimizationStatusIcon = (status: OptimizationStatus | undefined): ReactNode => {
    if (!status?.status) return null;

    const tooltip = getTooltipText(status);
    // Status and outcome are otherwise conveyed only by icon shape, colour, and
    // tooltip text, none of which a suite should key on.
    const stateAttrs = {
        'data-testid': TPO_OPTIMIZATION_STATUS_ICON,
        'data-state-status': status.status,
        'data-state-outcome': status.outcome ?? undefined,
    };

    switch (status.status) {
        case 'in_progress':
            return (
                <Tooltip title={tooltip} {...TOOLTIP_PLACEMENT_PROPS}>
                    <Spin indicator={<LoadingOutlined style={LOADING_ICON_STYLE} spin />} {...stateAttrs} />
                </Tooltip>
            );
        case 'error':
            return (
                <Tooltip title={tooltip} {...TOOLTIP_PLACEMENT_PROPS}>
                    <CloseCircleFilled style={ERROR_ICON_STYLE} {...stateAttrs} />
                </Tooltip>
            );
        case 'completed': {
            if (status.outcome === 'infeasible') {
                return (
                    <Tooltip title={tooltip} {...TOOLTIP_PLACEMENT_PROPS}>
                        <MinusCircleFilled style={INFEASIBLE_ICON_STYLE} {...stateAttrs} />
                    </Tooltip>
                );
            }
            const iconColor = getOutcomeColor(status.outcome);
            return (
                <Tooltip title={tooltip} {...TOOLTIP_PLACEMENT_PROPS}>
                    <CheckCircleFilled style={{ ...CHECK_ICON_STYLE, color: iconColor }} {...stateAttrs} />
                </Tooltip>
            );
        }
        default:
            return null;
    }
};
